import { z } from "zod/v4";
import { BasePathParamsSchema, defineEndpoint, defineResource, TenantPathParams } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { TenantScenarioStatusList } from "../fleet-messages/tenant-scenario-status-list";

// ─── List scenarios (GET /scenarios) ─────────────────────────────────────────

export const scenariosResource = defineResource({
  feature: FEATURE_NAME,
  resource: "scenarios",
  scope: "tenant",
});

export const ListScenariosPathParams = TenantPathParams;

export type ListScenariosPathParamsWire = z.input<typeof ListScenariosPathParams>;
export type ListScenariosPathParams = z.output<typeof ListScenariosPathParams>;

export { TenantScenarioStatusList as ListScenariosResponse };
export type { TenantScenarioStatusListWire as ListScenariosResponseWire } from "../fleet-messages/tenant-scenario-status-list";
export type { TenantScenarioStatusList as ListScenariosResponseDecoded } from "../fleet-messages/tenant-scenario-status-list";

export const listScenariosEndpoint = defineEndpoint(scenariosResource, {
  responseSchema: TenantScenarioStatusList,
});

// ─── Put scenario config (PUT /scenarios/{scenarioUuid}) ─────────────────────

const Coordinate = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

const FriendlyName = z.string().trim().min(1).max(140);

/**
 * Compass bearing in degrees, clockwise from true north.
 * `0` = N, `90` = E, `180` = S, `270` = W. Stored normalised into `[0, 360)`.
 */
const Direction = z.number().min(0).lt(360);

export const PutScenarioBody = z
  .object({
    coordinate: Coordinate.nullable().optional(),
    direction: Direction.nullable().optional(),
    friendlyName: FriendlyName.nullable().optional(),
    // Plain boolean toggle (no null/clear state) — absent means "leave as-is",
    // and an absent value on the row is treated as included by the report.
    excluded: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.coordinate !== undefined ||
      v.direction !== undefined ||
      v.friendlyName !== undefined ||
      v.excluded !== undefined,
    {
      message: "PUT body must include at least coordinate, direction, friendlyName, or excluded (use null to clear)",
    },
  )
  // Direction is meaningless without a coordinate — the UI never renders an
  // arrow without a dot to anchor it. Reject the well-formed bad case where
  // the same patch clears the coordinate and sets a bearing; the service
  // also implicitly clears `direction` when `coordinate` is null, but
  // catching it at the codec gives a clearer error to API callers.
  .refine((v) => !(v.coordinate === null && typeof v.direction === "number"), {
    message: "direction cannot be set in the same patch that clears coordinate",
    path: ["direction"],
  });

export type PutScenarioBodyWire = z.input<typeof PutScenarioBody>;
export type PutScenarioBody = z.output<typeof PutScenarioBody>;

export const PutScenarioResponse = z.object({
  scenarioUuid: z.string().min(1),
  coordinate: Coordinate.optional(),
  direction: Direction.optional(),
  friendlyName: FriendlyName.optional(),
  excluded: z.boolean().optional(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string(),
});

export type PutScenarioResponseWire = z.input<typeof PutScenarioResponse>;
export type PutScenarioResponse = z.output<typeof PutScenarioResponse>;

export const PutScenarioPathParams = BasePathParamsSchema.extend({ scenarioUuid: z.string().min(1) });
export type PutScenarioPathParamsWire = z.input<typeof PutScenarioPathParams>;
export type PutScenarioPathParams = z.output<typeof PutScenarioPathParams>;

const scenarioResource = defineResource({
  feature: FEATURE_NAME,
  resource: "scenarios/{scenarioUuid}",
  resourcePathSchema: z.object({ scenarioUuid: z.string().min(1) }),
  scope: "camera",
});

export const putScenarioEndpoint = defineEndpoint(scenarioResource, {
  method: "PUT",
  bodySchema: PutScenarioBody,
  responseSchema: PutScenarioResponse,
});
